"use client";

import { type ReactNode, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type Fields,
  MolarityAnswer,
} from "@/components/tools/chemistry/molarity-answer";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import {
  SUBSTANCE_STRINGS,
  SubstanceFields,
  SubstanceProblem,
} from "@/components/tools/chemistry/substance-fields";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { fewestSigFigs } from "@/lib/tool-kit/chemistry/format";
import {
  type MolaritySolve,
  type MolarityValues,
  solveMolarity,
} from "@/lib/tool-kit/chemistry/solutions";
import {
  resolveSubstance,
  type Substance,
  type SubstanceMode,
} from "@/lib/tool-kit/chemistry/substance";
import {
  MASS,
  MOLARITY,
  toBase,
  unitOptions,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const MOLARITY_CALCULATOR_STRINGS = {
  ...SUBSTANCE_STRINGS,
  solveLabel: "Solve for",
  solveMolarity: "Molarity",
  solveMass: "Mass of solute",
  solveVolume: "Volume of solution",
  solveMolarMass: "Molar mass",
  massLabel: "Mass of solute",
  volumeLabel: "Volume of solution",
  molarityLabel: "Molarity",
  answerSuffix: "{label} (answer)",
  massUnit: "Mass unit",
  volumeUnit: "Volume unit",
  molarityUnit: "Molarity unit",
  extrasLabel: "Optional: normality and molality",
  equivalents: "Equivalents per mole (for normality)",
  equivalentsHint: "2 for H₂SO₄ as an acid, 1 for HCl, 2 for Ca(OH)₂.",
  solventLabel: "Mass of solvent in kg (for molality)",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  answerMolarity: "Molarity",
  answerMass: "Mass of solute",
  answerVolume: "Volume of solution",
  answerMolarMass: "Molar mass",
  moles: "Amount of solute",
  normality: "Normality",
  molality: "Molality",
  recipeHeading: "How to make it",
  recipe:
    "Weigh out {mass} {massUnit} of {solute}. Dissolve it in less than {volume} {volumeUnit} of solvent, transfer it to a volumetric flask, and add solvent up to the {volume} {volumeUnit} mark. The result is {molarity} {molarityUnit}.",
  soluteGeneric: "the solute",
  working: "The working",
  convertLine: "{value} {unit} = {base} {baseUnit}",
  molesFromMass: "n = m ÷ M = {mass} g ÷ {molar} g/mol = {moles} mol",
  molesFromConc: "n = c × V = {conc} mol/L × {volume} L = {moles} mol",
  concLine: "c = n ÷ V = {moles} mol ÷ {volume} L = {conc} mol/L",
  massLine: "m = n × M = {moles} mol × {molar} g/mol = {mass} g",
  volumeLine: "V = n ÷ c = {moles} mol ÷ {conc} mol/L = {volume} L",
  molarLine: "M = m ÷ n = {mass} g ÷ {moles} mol = {molar} g/mol",
  normalityLine: "N = c × eq = {conc} mol/L × {eq} = {normality} eq/L",
  molalityLine:
    "b = n ÷ m(solvent) = {moles} mol ÷ {solvent} kg = {molality} mol/kg",
  enterValues: "Enter the other three quantities to see the answer.",
  invalid:
    "Each quantity must be a number greater than zero. Check the entries above.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value} {unit}",
  unitMolar: "mol/L",
} as const;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;
const MASS_UNITS = unitOptions(MASS).filter((u) =>
  ["ug", "mg", "g", "kg"].includes(u.value)
);
const VOLUME_UNITS = unitOptions(VOLUME).filter((u) =>
  ["uL", "mL", "L"].includes(u.value)
);
const MOLARITY_UNITS = unitOptions(MOLARITY);

/** A typed quantity in base units, "missing" when blank, "invalid" when unusable. */
function readBase(
  text: string,
  unit: string,
  family: typeof MASS
): number | "missing" | "invalid" {
  if (text.trim() === "") {
    return "missing";
  }
  const value = parseMeasuredNumber(text);
  if (value === null || !(value > 0)) {
    return "invalid";
  }
  return toBase(value, family, unit);
}

type Evaluation =
  | { status: "missing" | "invalid" }
  | { status: "ok"; values: MolarityValues };

/** Read the three quantities that are not being solved for and solve the fourth. */
function evaluate(
  solve: MolaritySolve,
  fields: Fields,
  substance: Substance
): Evaluation {
  const mass = readBase(fields.massText, fields.massUnit, MASS);
  const volume = readBase(fields.volumeText, fields.volumeUnit, VOLUME);
  const molarity = readBase(fields.molarityText, fields.molarityUnit, MOLARITY);
  const needed = [
    ...(solve === "mass" ? [] : [mass]),
    ...(solve === "volume" ? [] : [volume]),
    ...(solve === "molarity" ? [] : [molarity]),
  ];
  if (needed.includes("missing")) {
    return { status: "missing" };
  }
  if (needed.includes("invalid")) {
    return { status: "invalid" };
  }
  const values = solveMolarity(solve, {
    massG: typeof mass === "number" ? mass : undefined,
    volumeL: typeof volume === "number" ? volume : undefined,
    molarity: typeof molarity === "number" ? molarity : undefined,
    molarMass: substance.kind === "ok" ? substance.molarMass : undefined,
  });
  return values === null ? { status: "invalid" } : { status: "ok", values };
}

/** The typed numbers that limit the answer's significant figures. */
function limitingEntries(
  solve: MolaritySolve,
  fields: Fields,
  molarMassText: string,
  usesTypedMolarMass: boolean
): string[] {
  return [
    ...(solve === "mass" ? [] : [fields.massText]),
    ...(solve === "volume" ? [] : [fields.volumeText]),
    ...(solve === "molarity" ? [] : [fields.molarityText]),
    ...(usesTypedMolarMass ? [molarMassText] : []),
  ];
}

function positiveOrNull(text: string): number | null {
  if (text.trim() === "") {
    return null;
  }
  const value = parseMeasuredNumber(text);
  return value !== null && value > 0 ? value : null;
}

export default function MolarityCalculatorWidget() {
  const strings = useToolUi(MOLARITY_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [solve, setSolve] = useState<MolaritySolve>("mass");
  const [mode, setMode] = useState<SubstanceMode>("formula");
  const [formula, setFormula] = useState("NaCl");
  const [molarMassText, setMolarMassText] = useState("");
  const [fields, setFields] = useState<Fields>({
    massText: "",
    massUnit: "g",
    volumeText: "250.0",
    volumeUnit: "mL",
    molarityText: "0.1000",
    molarityUnit: "M",
  });
  const [eqText, setEqText] = useState("");
  const [solventText, setSolventText] = useState("");
  const [sigChoice, setSigChoice] = useState("match");
  const set = (patch: Partial<Fields>) => setFields({ ...fields, ...patch });

  const needsSubstance = solve !== "molarMass";
  const substance = resolveSubstance(mode, formula, molarMassText);
  const evaluation = evaluate(solve, fields, substance);
  const typed = limitingEntries(
    solve,
    fields,
    molarMassText,
    needsSubstance && mode === "mass"
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);

  let body: ReactNode;
  if (needsSubstance && substance.kind !== "ok") {
    body = <SubstanceProblem strings={strings} substance={substance} />;
  } else if (evaluation.status === "missing") {
    body = <Notice>{strings.enterValues}</Notice>;
  } else if (evaluation.status === "ok") {
    body = (
      <MolarityAnswer
        eq={positiveOrNull(eqText)}
        fields={fields}
        sig={sig}
        solve={solve}
        solvent={positiveOrNull(solventText)}
        strings={strings}
        substance={substance}
        v={evaluation.values}
      />
    );
  } else {
    body = <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }

  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="mol-solve"
        onChange={setSolve}
        options={[
          { value: "molarity", label: strings.solveMolarity },
          { value: "mass", label: strings.solveMass },
          { value: "volume", label: strings.solveVolume },
          { value: "molarMass", label: strings.solveMolarMass },
        ]}
        value={solve}
      />
      {needsSubstance ? (
        <SubstanceFields
          formula={formula}
          formulaPlaceholder="NaCl"
          id="mol"
          mode={mode}
          molarMass={molarMassText}
          molarMassPlaceholder="58.44"
          onFormulaChange={setFormula}
          onModeChange={setMode}
          onMolarMassChange={setMolarMassText}
          strings={strings}
        />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityInput
          id="mol-mass"
          label={
            solve === "mass"
              ? t(strings.answerSuffix, { label: strings.massLabel })
              : strings.massLabel
          }
          onUnitChange={(massUnit) => set({ massUnit })}
          onValueChange={(massText) => set({ massText })}
          solved={solve === "mass"}
          unit={fields.massUnit}
          unitLabel={strings.massUnit}
          units={MASS_UNITS}
          value={fields.massText}
        />
        <QuantityInput
          id="mol-volume"
          label={
            solve === "volume"
              ? t(strings.answerSuffix, { label: strings.volumeLabel })
              : strings.volumeLabel
          }
          onUnitChange={(volumeUnit) => set({ volumeUnit })}
          onValueChange={(volumeText) => set({ volumeText })}
          solved={solve === "volume"}
          unit={fields.volumeUnit}
          unitLabel={strings.volumeUnit}
          units={VOLUME_UNITS}
          value={fields.volumeText}
        />
        <QuantityInput
          id="mol-molarity"
          label={
            solve === "molarity"
              ? t(strings.answerSuffix, { label: strings.molarityLabel })
              : strings.molarityLabel
          }
          onUnitChange={(molarityUnit) => set({ molarityUnit })}
          onValueChange={(molarityText) => set({ molarityText })}
          solved={solve === "molarity"}
          unit={fields.molarityUnit}
          unitLabel={strings.molarityUnit}
          units={MOLARITY_UNITS}
          value={fields.molarityText}
        />
        <SelectField
          id="mol-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.equivalentsHint}
          id="mol-eq"
          label={strings.equivalents}
          numeric
          onChange={setEqText}
          value={eqText}
        />
        <TextField
          id="mol-solvent"
          label={strings.solventLabel}
          numeric
          onChange={setSolventText}
          value={solventText}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
